import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";
import { $api, fetchClient } from "@/lib/api-client";

const PAGE_SIZE = 10;

/**
 * Infinite-scroll list of verified reviews for a product. `GET
 * /products/{productId}/reviews` is fully paginated server-side; this
 * consumes it page-by-page instead of relying on backend defaults (page=1,
 * limit=10), which silently hid every review past the first page (same
 * pattern as `useFetchProducts`, `useLaunchpadListings`, etc.).
 */
export const useProductReviews = (productId: string) => {
  const t = useTranslations("shop");
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAuth();

  const reviewsQuery = useInfiniteQuery({
    queryKey: ["get", "/products/{productId}/reviews", productId, "infinite"],
    queryFn: async ({ pageParam }) => {
      const response = await fetchClient.GET("/products/{productId}/reviews", {
        params: {
          path: { productId },
          query: { page: pageParam, limit: PAGE_SIZE },
        },
      });
      const error = response.error;
      if (error) throw error;
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;
      if (pagination?.hasNextPage) return pagination.page + 1;
      return undefined;
    },
    enabled: !!productId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const myReviewQuery = $api.useQuery(
    "get",
    "/products/{productId}/reviews/mine",
    { params: { path: { productId } } },
    { enabled: !!productId && isLoggedIn },
  );

  const submitMutation = $api.useMutation(
    "post",
    "/products/{productId}/reviews",
    {
      onSuccess: () => {
        toast.success(t("reviews.toasts.submitted"), {
          description: t("reviews.toasts.submittedDescription"),
        });
        queryClient.invalidateQueries({
          queryKey: ["get", "/products/{productId}/reviews", productId],
        });
        queryClient.invalidateQueries({
          queryKey: ["get", "/products/{productId}/reviews/mine"],
        });
      },
      onError: () => {
        toast.error(t("reviews.toasts.error"));
      },
    },
  );

  return {
    ...reviewsQuery,
    reviews:
      reviewsQuery.data?.pages.flatMap((page) => page?.data?.result ?? []) ??
      [],
    summary: reviewsQuery.data?.pages[0]?.data?.summary ?? {
      average: 0,
      count: 0,
    },
    myReview: myReviewQuery.data?.data ?? null,
    submit: submitMutation,
    isLoggedIn,
  };
};
